"use client";

import { useState, useEffect, useCallback, Fragment, useMemo } from "react";
import { Dialog, Transition } from "@headlessui/react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  UserPlus,
  X,
  Loader2,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  User,
  Globe,
  FileText,
  Building,
  Mail,
  Phone,
  Tag,
  Hash,
} from "lucide-react";
import { SearchableSelect, InputCustom } from "../../components";
import leadsService from "../../services/leadsService";
import { countryOptions } from '../../utils/data/CountryDialCodes'

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------
const ErrorText = ({ children }) =>
  children ? (
    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
      <AlertCircle size={12} />
      {children}
    </p>
  ) : null;

// static select options (translated at render)
const RAW_CLIENT_TYPES = [
  { id: 1, name: "Advertiser", value: "advertiser" },
  { id: 2, name: "Agency", value: "agency" },
  { id: 3, name: "Partner", value: "partner" },
  { id: 4, name: "Reseller", value: "reseller" },
  { id: 5, name: "Affiliate", value: "affiliate" },
];

const RAW_STATUS = [
     { id: 1, name: "Active", value: "active" },
    { id: 2, name: "Inactive", value: "inactive" },
    { id: 3, name: "Pending", value: "pending" },
    { id: 4, name: "Suspended", value: "suspended" },  
    { id: 5, name: "Frozen", value: "frozen" },
    { id: 6, name: "Blacklisted", value: "blacklisted" },
];


const RAW_COUNTRIES = countryOptions
// ------------------------------------------------------------------
// Component
// ------------------------------------------------------------------
const ConvertToClientModal = ({ isOpen, onClose, leadId, onSuccess }) => {
  const { t } = useTranslation();

  const clientTypeOptions = useMemo(
    () => RAW_CLIENT_TYPES.map((o) => ({ ...o, name: t(o.name) })),
    [t]
  );
  const statusOptions = useMemo(
    () => RAW_STATUS.map((o) => ({ ...o, name: t(o.name) })),
    [t]
  );
  const countryOptions = useMemo(
    () => RAW_COUNTRIES.map((o) => ({ ...o, name: t(o.name) })),
    [t]
  );

  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState(1);
  const totalSteps = 2;
  const [sendEmail, setSendEmail] = useState(false);
  const [leadData, setLeadData] = useState(null);

  const [clientData, setClientData] = useState({
    fullName: "",
    email: "",
    phone: "",
    businessName: "",
    website: "",
    userType: "advertiser",
    status: "active",
    country: "",
    notes: "",
    clientId: "",
    reference: "",
    telegram: "",
  });

  const [errors, setErrors] = useState({});

  // reset form (when closing)
  const resetForm = useCallback(() => {
    setStep(1);
    setSendEmail(false);
    setLeadData(null);
    setClientData({
      fullName: "",
      email: "",
      phone: "",
      businessName: "",
      website: "",
      userType: "advertiser",
      status: "active",
      country: "",
      notes: "",
      clientId: "",
      reference: "",
      telegram: "",
    });
    setErrors({});
  }, []);

  // fetch lead
  const fetchLeadData = useCallback(async () => {
    if (!leadId) return;
    try {
      setLoading(true);
      const res = await leadsService.get(leadId);
      if (res.data?.statusCode === 200 && res.data?.data) {
        const lead = res.data.data;
        setLeadData(lead);
        // prefill
        setClientData((prev) => ({
          ...prev,
          fullName: lead.name || "",
          email: lead.email || "",
          phone: lead.phone || "",
          businessName: lead.company || "",
          website: lead.store || "",
          telegram: lead.telegram || "",
        }));
      } else {
        toast.error(t("Failed to load lead data"));
      }
    } catch (e) {
      console.error("Error fetching lead:", e);
      toast.error(t("Failed to load lead data"));
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  // modal open/close lifecycle
  useEffect(() => {
    if (!isOpen) {
      resetForm();
      return;
    }
    fetchLeadData();
  }, [isOpen, fetchLeadData, resetForm]);

  const handleChange = (field, value) => {
    setClientData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateStep = (s) => {
    const e = {};
    const v = (x) => String(x ?? "").trim();

    if (s === 2) {
      if (!v(clientData.fullName)) e.fullName = t("Full name is required");
      if (!v(clientData.email)) e.email = t("Email is required");
      else if (!/\S+@\S+\.\S+/.test(clientData.email)) e.email = t("Email is invalid");
      if (!v(clientData.businessName)) e.businessName = t("Business name is required");
      if (!v(clientData.userType)) e.userType = t("Client type is required");
      if (!v(clientData.status)) e.status = t("Status is required");
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = async () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      // final submit
      if (validateStep(2)) {
        await handleConvert();
      }
    }
  };

  const handleConvert = async () => {
    try {
      setProcessing(true);

      const payload = {
        leadId,
        fullName: clientData.fullName,
        email: clientData.email,
        phone: clientData.phone,
        businessName: clientData.businessName,
        website: clientData.website,
        userType: clientData.userType,
        status: clientData.status,
        country: clientData.country,
        notes: clientData.notes,
        clientId: clientData.clientId,
        reference: clientData.reference,
        telegram: clientData.telegram,
        sendWelcomeEmail: sendEmail,
      };

      const res = await leadsService.convert(leadId, payload);

      if (res.data) {
        toast.success(t("Lead successfully converted to client"));
        onSuccess && onSuccess();
        onClose();
      } else {
        toast.error(res.data?.message || t("Failed to convert lead to client"));
      }
    } catch (error) {
      console.error("Conversion failed:", error);
      toast.error(error.response?.data?.message || t("Failed to convert lead to client"));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-xl bg-white dark:bg-neutral-950 shadow-xl transition-all">
                {/* Header */}
                <div className="border-b border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-5">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-medium text-neutral-900 dark:text-white flex items-center gap-2">
                      <UserPlus className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
                      {t("Convert Lead to Client")}
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="p-1.5 rounded-full text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors"
                      type="button"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  {/* Progress */}
                  <div className="mt-6 flex items-center gap-2">
                    {Array.from({ length: totalSteps }).map((_, i) => (
                      <div
                        key={i}
                        className={`h-1.5 rounded-full transition-all ${i + 1 <= step
                          ? "bg-yellow-500 dark:bg-yellow-400 flex-grow"
                          : "bg-neutral-200 dark:bg-neutral-700 w-6"
                          }`}
                      />
                    ))}
                  </div>
                  <div className="mt-2 text-neutral-600 dark:text-neutral-400 text-sm">
                    {step === 1 ? t("Step 1: Confirm Lead Details") : t("Step 2: Complete Client Information")}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[calc(100vh-300px)] overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 size={30} className="animate-spin text-neutral-600 dark:text-neutral-400" />
                    </div>
                  ) : !leadData ? (
                    <div className="text-center py-6">
                      <AlertCircle size={40} className="mx-auto mb-4 text-red-500" />
                      <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">{t("Lead Not Found")}</h3>
                      <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                        {t("The selected lead could not be found. Please try again.")}
                      </p>
                      <button
                        onClick={onClose}
                        className="px-4 py-2 bg-neutral-200 dark:bg-neutral-700 text-neutral-800 dark:text-white rounded-xl hover:bg-neutral-300 dark:hover:bg-neutral-600"
                      >
                        {t("Close")}
                      </button>
                    </div>
                  ) : (
                    <>
                      {step === 1 && (
                        <div className="space-y-4">
                          <h3 className="text-lg font-medium text-neutral-900 dark:text-white">{t("Lead Information")}</h3>
                          <p className="text-sm text-neutral-600 dark:text-neutral-400">
                            {t("Please confirm the lead details before converting to a client.")}
                          </p>

                          <div className="bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl p-4 space-y-3">
                            <div className="flex justify-between">
                              <span className="text-sm text-neutral-500 dark:text-neutral-400">{t("Name")}:</span>
                              <span className="text-sm font-medium text-neutral-900 dark:text-white">{leadData.name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-neutral-500 dark:text-neutral-400">{t("Company")}:</span>
                              <span className="text-sm font-medium text-neutral-900 dark:text-white">{leadData.company}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-neutral-500 dark:text-neutral-400">{t("Email")}:</span>
                              <span className="text-sm font-medium text-neutral-900 dark:text-white">{leadData.email}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-neutral-500 dark:text-neutral-400">{t("Platform")}:</span>
                              <span className="text-sm font-medium text-neutral-900 dark:text-white">
                                {leadData.platform?.charAt(0).toUpperCase() + leadData.platform?.slice(1)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-neutral-500 dark:text-neutral-400">{t("Monthly Spend")}:</span>
                              <span className="text-sm font-medium text-neutral-900 dark:text-white">{leadData.monthlySpend}</span>
                            </div>

                            {leadData.store && (
                              <div className="flex justify-between">
                                <span className="text-sm text-neutral-500 dark:text-neutral-400">{t("Website")}:</span>
                                <span className="text-sm font-medium text-neutral-900 dark:text-white">{leadData.store}</span>
                              </div>
                            )}

                            {leadData.phone && (
                              <div className="flex justify-between">
                                <span className="text-sm text-neutral-500 dark:text-neutral-400">{t("Whatsapp Number")}:</span>
                                <span className="text-sm font-medium text-neutral-900 dark:text-white">{leadData.phone}</span>
                              </div>
                            )}

                            {leadData.telegram && (
                              <div className="flex justify-between">
                                <span className="text-sm text-neutral-500 dark:text-neutral-400">{t("Telegram Username")}:</span>
                                <span className="text-sm font-medium text-neutral-900 dark:text-white">{leadData.telegram}</span>
                              </div>
                            )}

                            {leadData.category && (
                              <div className="flex justify-between">
                                <span className="text-sm text-neutral-500 dark:text-neutral-400">{t("Category")}:</span>
                                <span className="text-sm font-medium text-neutral-900 dark:text-white">{leadData.category}</span>
                              </div>
                            )}
                          </div>

                          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-100 dark:border-yellow-800/30">
                            <div className="flex items-start gap-2">
                              <AlertCircle size={16} className="text-yellow-600 dark:text-yellow-400 mt-0.5" />
                              <div>
                                <h4 className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
                                  {t("Important Note")}
                                </h4>
                                <p className="text-xs text-yellow-600 dark:text-yellow-300 mt-1">
                                  {t(
                                    "Converting this lead to a client will move them from your leads pipeline to your clients list. This action cannot be undone."
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {step === 2 && (
                        <div className="space-y-6">
                          <div>
                            <h3 className="text-lg font-medium text-neutral-900 dark:text-white">
                              {t("Complete Client Information")}
                            </h3>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400">
                              {t("Please provide additional client information.")}
                            </p>
                          </div>

                          {/* Basic Information */}
                          <div className="space-y-4">
                            <h4 className="text-sm font-medium text-neutral-900 dark:text-white">{t("Basic Information")}</h4>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Reference */}
                              <div className="space-y-1">
                                <InputCustom
                                  type="url"
                                  label={t("Client Reference")}
                                  placeholder={t("Enter client reference")}
                                  value={clientData.reference}
                                  onChange={(v) => handleChange("reference", v)}
                                  icon={<Globe size={16} className="text-neutral-500 dark:text-neutral-400" />}
                                />
                              </div>

                              {/* Client ID */}
                              <div className="space-y-1">
                                <InputCustom
                                  label={t("Client ID")}
                                  placeholder={t("Enter client ID")}
                                  value={clientData.clientId}
                                  onChange={(v) => handleChange("clientId", v)}
                                  icon={<Hash size={16} className="text-neutral-500 dark:text-neutral-400" />}
                                />
                              </div>

                              {/* Company */}
                              <div className="space-y-1">
                                <InputCustom
                                  label={t("Company Name")}
                                  placeholder={t("Enter company name")}
                                  value={clientData.businessName}
                                  onChange={(v) => handleChange("businessName", v)}
                                  icon={<Building size={16} className="text-neutral-500 dark:text-neutral-400" />}
                                />
                                <ErrorText>{errors.businessName}</ErrorText>
                              </div>

                              {/* Email */}
                              <div className="space-y-1">
                                <InputCustom
                                  type="email"
                                  label={t("Email Address")}
                                  placeholder={t("Enter email address")}
                                  value={clientData.email}
                                  onChange={(v) => handleChange("email", v)}
                                  icon={<Mail size={16} className="text-neutral-500 dark:text-neutral-400" />}
                                />
                                <ErrorText>{errors.email}</ErrorText>
                              </div>

                              {/* Phone */}
                              <div className="space-y-1">
                                <InputCustom
                                  type="tel"
                                  label={t("Whatsapp Number")}
                                  placeholder={t("Enter phone number")}
                                  value={clientData.phone}
                                  onChange={(v) => handleChange("phone", v)}
                                  icon={<Phone size={16} className="text-neutral-500 dark:text-neutral-400" />}
                                />
                              </div>

                              {/* Telegram */}
                              <div className="space-y-1">
                                <InputCustom
                                  type="text"
                                  label={t("Telegram")}
                                  placeholder={t("Enter client telegram")}
                                  value={clientData.telegram}
                                  onChange={(v) => handleChange("telegram", v)}
                                  icon={<User size={16} className="text-neutral-500 dark:text-neutral-400" />}
                                />
                              </div>

                              {/* Country */}
                              <div className="space-y-1">
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                  {t("Client Country")}
                                </label>
                                <SearchableSelect
                                  options={countryOptions}
                                  value={clientData.country}
                                  onChange={(v) => handleChange("country", v)}
                                  placeholder={t("Select country")}
                                  icon={<Globe size={16} />}
                                />
                              </div>
   {/* Website */}
                              <div className="space-y-1">
                                <InputCustom
                                  type="text"
                                  label={t("Website")}
                                  placeholder={t("Enter client Website")}
                                  value={clientData.website}
                                  onChange={(v) => handleChange("website", v)}
                                  icon={<Globe size={16} className="text-neutral-500 dark:text-neutral-400" />}
                                />
                              </div>
                              
                            </div>
                          </div>

                          {/* Additional Details */}
                          <div className="space-y-4">
                            <h4 className="text-sm font-medium text-neutral-900 dark:text-white">{t("Additional Details")}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Client Type */}
                              <div className="space-y-1">
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                  {t("Client Type")}*
                                </label>
                                <SearchableSelect
                                  options={clientTypeOptions}
                                  value={clientData.userType}
                                  onChange={(v) => handleChange("userType", v)}
                                  placeholder={t("Select client type")}
                                  icon={<Tag size={16} />}
                                />
                                <ErrorText>{errors.userType}</ErrorText>
                              </div>

                              {/* Status */}
                              <div className="space-y-1">
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                  {t("Status")}*
                                </label>
                                <SearchableSelect
                                  options={statusOptions}
                                  value={clientData.status}
                                  onChange={(v) => handleChange("status", v)}
                                  placeholder={t("Select status")}
                                  icon={<AlertCircle size={16} />}
                                />
                                <ErrorText>{errors.status}</ErrorText>
                              </div>
                            </div>
                          </div>

                          {/* Notes */}
                          <div className="space-y-1">
                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                              {t("Notes")}
                            </label>
                            <div className="relative">
                              <div className="absolute top-3 left-3 pointer-events-none">
                                <FileText size={16} className="text-neutral-500 dark:text-neutral-400" />
                              </div>
                              <textarea
                                value={clientData.notes}
                                onChange={(e) => handleChange("notes", e.target.value)}
                                rows={3}
                                placeholder={t("Add any additional information about this client...")}
                                className="w-full pl-9 pt-2 pr-3 border text-sm border-neutral-300 dark:bg-neutral-800 hover:border-neutral-400 dark:hover:border-neutral-600 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                              />
                            </div>
                          </div>

                          {/* Send Email */}
                          <div className="flex items-center mt-6">
                            <input
                              id="send-welcome"
                              type="checkbox"
                              checked={sendEmail}
                              onChange={() => setSendEmail((v) => !v)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 rounded"
                            />
                            <label htmlFor="send-welcome" className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300">
                              {t("Send welcome email to client")}
                            </label>
                          </div>

                          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-100 dark:border-green-800/30">
                            <div className="flex items-start gap-2">
                              <CheckCircle size={16} className="text-green-600 dark:text-green-400 mt-0.5" />
                              <div>
                                <h4 className="text-sm font-medium text-green-700 dark:text-green-400">{t("Ready to Convert")}</h4>
                                <p className="text-xs text-green-600 dark:text-green-300 mt-1">
                                  {`You're all set to convert ${leadData.name || "this lead"}${leadData.company ? ` from ${leadData.company}` : ""} to a client.`}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Footer */}
                {!loading && (
                  <div className="border-t border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-5 flex justify-between">
                    <button
                      type="button"
                      onClick={step === 1 ? onClose : () => setStep((s) => Math.max(1, s - 1))}
                      className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-xl hover:bg-neutral-50 dark:bg-transparent dark:text-neutral-300 dark:border-neutral-600 dark:hover:bg-neutral-800 focus:outline-none"
                      disabled={processing}
                    >
                      {step === 1 ? t("Cancel") : t("Back")}
                    </button>
                    <button
                      type="button"
                      onClick={step === 2 ? () => validateStep(2) && next() : next}
                      disabled={processing || (!leadData && step === 1)}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {processing ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t("Processing...")}
                        </>
                      ) : (
                        <>
                          {step === totalSteps ? t("Convert to Client") : t("Next")}
                          {step !== totalSteps && <ArrowRight className="ml-2 h-4 w-4" />}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ConvertToClientModal;
